import React from 'react';
import { useVoucherSummary } from '../useVoucherSummary';

/**
 * A simplified, highly visual Daybook component for non-accountants using Bootstrap 5.
 * @param {Object} props
 * @param {Array} props.voucherList - Array of raw voucher objects
 */
const DashboardSummary = ({ voucherList = [] }) => {
  const summary = useVoucherSummary({ voucherList });

  // Simple Indian Currency formatter
  const formatMoney = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="">

      {/* SECTION 1: SALES REVENUE (MONEY COMING IN) */}
      <div className="card shadow-sm border-0 mb-4 overflow-hidden">
        <div className="card-header bg-success text-white py-3">
          <h5 className="mb-0 fw-bold">📈 Sales Revenue (Customer Invoices)</h5>
        </div>
        <div className="card-body p-0">
          <div className="row g-0 text-center text-sm-start p-3 align-items-center">
            <div className="col-12 col-sm-6 border-sm-end mb-3 mb-sm-0 ps-3">
              <span className="text-muted small d-block">Sales Value Before Tax</span>
              <h4 className="fw-semibold text-secondary">{formatMoney(summary.totalTaxableSale)}</h4>
            </div>
            <div className="col-12 col-sm-6 ps-sm-4 bg-light-subtle py-2 rounded">
              <span className="text-success small d-block fw-bold">Total Sales Collected (With Tax)</span>
              <h2 className="fw-bold text-success">{formatMoney(summary.totalSale)}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: PURCHASES (MONEY GOING OUT) */}
      <div className="card shadow-sm border-0 mb-4 overflow-hidden">
        <div className="card-header bg-danger text-white py-3">
          <h5 className="mb-0 fw-bold">📉 Purchases (Supplier Bills)</h5>
        </div>
        <div className="card-body p-0">
          <div className="row g-0 text-center text-sm-start p-3 align-items-center">
            <div className="col-12 col-sm-6 border-sm-end mb-3 mb-sm-0 ps-3">
              <span className="text-muted small d-block">Purchase Value Before Tax</span>
              <h4 className="fw-semibold text-secondary">{formatMoney(summary.totalTaxablePurchase)}</h4>
            </div>
            <div className="col-12 col-sm-6 ps-sm-4 bg-light-subtle py-2 rounded">
              <span className="text-danger small d-block fw-bold">Total Purchases Owed (With Tax)</span>
              <h2 className="fw-bold text-danger">{formatMoney(summary.totalPurchase)}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 3: ACTUAL CASH & BANK MOVEMENTS */}
      <div className="row g-4 mb-4">
        {/* Payment In */}
        <div className="col-12 col-md-6">
          <div className="card shadow-sm border-0 h-100 border-start border-success border-4">
            <div className="card-body">
              <span className="text-muted small d-block text-uppercase fw-bold tracking-wider">💰 Actual Payment Received (In)</span>
              <p className="text-muted small mb-2">Real cash/bank collected from customers</p>
              <h3 className="fw-bold text-success mb-0">{formatMoney(summary.totalPaymentIn)}</h3>
            </div>
          </div>
        </div>
        {/* Payment Out */}
        <div className="col-12 col-md-6">
          <div className="card shadow-sm border-0 h-100 border-start border-danger border-4">
            <div className="card-body">
              <span className="text-muted small d-block text-uppercase fw-bold tracking-wider">💸 Actual Payment Paid (Out)</span>
              <p className="text-muted small mb-2">Real cash/bank paid out to suppliers or utilities</p>
              <h3 className="fw-bold text-danger mb-0">{formatMoney(summary.totalPaymentOut)}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 4: TAX ESTIMATES */}
      <div className="card shadow-sm border-0 mb-4 bg-dark text-white">
        <div className="card-body p-4">
          <h6 className="fw-bold text-uppercase tracking-wide mb-3 text-warning">🏛️ Government Tax (GST) Summary</h6>
          <div className="row g-3">
            <div className="col-6 border-end border-secondary">
              <span className="text-light-emphasis small d-block">Tax Paid on Purchases (Tax Input)</span>
              <h4 className="fw-bold text-info mb-0">{formatMoney(summary.totalTaxInput)}</h4>
              <small className="text-muted d-block mt-1">Claimable back from Govt</small>
            </div>
            <div className="col-6 ps-3">
              <span className="text-light-emphasis small d-block">Tax Collected on Sales (Tax Output)</span>
              <h4 className="fw-bold text-warning mb-0">{formatMoney(summary.totalTaxOutput)}</h4>
              <small className="text-muted d-block mt-1">Payable to the Govt</small>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 5: RETURNS & ADJUSTMENTS */}
      <div className="row g-4 mb-4">
        {/* Credit Notes */}
        <div className="col-12 col-md-6">
          <div className="card shadow-sm border-0 bg-white">
            <div className="card-body">
              <span className="fw-bold d-block text-dark">🔄 Sales Returns (Credit Notes)</span>
              <p className="text-muted small mb-2">Refunds or discounts given to customers after selling</p>
              <h4 className="fw-bold text-dark mb-0">{formatMoney(summary.totalCreditNote)}</h4>
            </div>
          </div>
        </div>
        {/* Debit Notes */}
        <div className="col-12 col-md-6">
          <div className="card shadow-sm border-0 bg-white">
            <div className="card-body">
              <span className="fw-bold d-block text-dark">↩️ Purchase Returns (Debit Notes)</span>
              <p className="text-muted small mb-2">Refunds or discounts received from suppliers after buying</p>
              <h4 className="fw-bold text-dark mb-0">{formatMoney(summary.totalDebitNote)}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 6: INTERNAL BOOKKEEPING (CONTRA & JOURNAL) */}
      <div className="card shadow-sm border-0 bg-light-subtle border">
        <div className="card-header bg-secondary text-white py-2">
          <small className="fw-bold text-uppercase">⚙️ Internal Audits & Accounts Balancing</small>
        </div>
        <div className="card-body py-3">
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <span className="fw-semibold d-block text-dark small">🏦 Cash Transfers (Contra Vouchers)</span>
              <p className="text-muted fs-7 mb-1">Money shuffled inside your business (e.g., Drawing cash out of Bank, or depositing shop cash into Bank).</p>
              <span className="fw-bold text-secondary">{formatMoney(summary.totalContra)}</span>
            </div>
            <div className="col-12 col-sm-6 border-sm-start ps-sm-4">
              <span className="fw-semibold d-block text-dark small">📝 Bookkeeping Adjustments (Journal Vouchers)</span>
              <p className="text-muted fs-7 mb-1">Manual non-cash adjustments used by accountants to clear mismatched balances or correct minor mistakes.</p>
              <span className="fw-bold text-secondary">{formatMoney(summary.totalJournal)}</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DashboardSummary;